import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent } from 'react'
import { useLongPress } from '../hooks/useLongPress'

export interface UseTileTapGesturesOptions {
  isMobile: boolean
  onSingleTap: () => void
  onDoubleTap: () => void
  onLongPress: () => void
  disabledLongPress?: boolean
  disabledDoubleTap?: boolean
  onDesktopClick: () => void
  onDesktopDoubleClick?: () => void
  doubleTapWindowMs?: number
  driftPx?: number
}

/**
 * Custom tap gesture handler for BoardExplorer tiles.
 *
 * Implements mobile-specific inverted tap semantics:
 * - Desktop: single-click selects/switches, double-click drills in / goes up.
 * - Mobile: single-tap drills in / goes up, double-tap and long-press open details.
 * - Back tile on mobile: actively suppresses double-tap (early return) and long-press (disabled: true).
 */
export function useTileTapGestures({
  isMobile,
  onSingleTap,
  onDoubleTap,
  onLongPress,
  disabledLongPress = false,
  disabledDoubleTap = false,
  onDesktopClick,
  onDesktopDoubleClick,
  doubleTapWindowMs = 300,
  driftPx = 4,
}: UseTileTapGesturesOptions) {
  const suppressClickRef = useRef(false)
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null)
  const singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)

  const onSingleTapRef = useRef(onSingleTap)
  const onDoubleTapRef = useRef(onDoubleTap)
  const onLongPressRef = useRef(onLongPress)
  const onDesktopClickRef = useRef(onDesktopClick)
  const onDesktopDoubleClickRef = useRef(onDesktopDoubleClick)

  useEffect(() => {
    onSingleTapRef.current = onSingleTap
    onDoubleTapRef.current = onDoubleTap
    onLongPressRef.current = onLongPress
    onDesktopClickRef.current = onDesktopClick
    onDesktopDoubleClickRef.current = onDesktopDoubleClick
  })

  const clearSingleTapTimer = useCallback(() => {
    if (singleTapTimerRef.current !== null) {
      clearTimeout(singleTapTimerRef.current)
      singleTapTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearSingleTapTimer()
    }
  }, [clearSingleTapTimer])

  const longPress = useLongPress({
    ms: 450,
    driftPx,
    disabled: !isMobile || disabledLongPress,
    onLongPress: () => {
      if (!isMobile || disabledLongPress) return
      suppressClickRef.current = true
      clearSingleTapTimer()
      lastTapRef.current = null
      onLongPressRef.current()
    },
  })

  const onPointerDown = useCallback((event: PointerEvent) => {
    if (!isMobile) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    suppressClickRef.current = false
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    longPress.onPointerDown(event)
  }, [isMobile, longPress])

  const onPointerMove = useCallback((event: PointerEvent) => {
    if (!isMobile) return
    longPress.onPointerMove(event)
  }, [isMobile, longPress])

  const onPointerUp = useCallback(() => {
    if (!isMobile) return
    longPress.onPointerUp()
  }, [isMobile, longPress])

  const onPointerCancel = useCallback(() => {
    if (!isMobile) return
    longPress.onPointerCancel()
  }, [isMobile, longPress])

  const onClick = useCallback((event: MouseEvent) => {
    if (!isMobile) {
      onDesktopClickRef.current()
      return
    }

    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }

    const now = Date.now()
    const lastTap = lastTapRef.current
    const clientX = typeof event.clientX === 'number'
      ? event.clientX
      : (pointerStartRef.current?.x ?? 0)
    const clientY = typeof event.clientY === 'number'
      ? event.clientY
      : (pointerStartRef.current?.y ?? 0)

    const isWithinDrift = lastTap
      ? Math.hypot(clientX - lastTap.x, clientY - lastTap.y) <= driftPx
      : false
    const isDoubleTap = Boolean(lastTap && (now - lastTap.time) < doubleTapWindowMs && isWithinDrift)

    if (isDoubleTap) {
      lastTapRef.current = null
      clearSingleTapTimer()
      if (disabledDoubleTap) {
        // Actively suppress double tap (early return)
        return
      }
      onDoubleTapRef.current()
    } else {
      lastTapRef.current = { time: now, x: clientX, y: clientY }
      clearSingleTapTimer()
      singleTapTimerRef.current = setTimeout(() => {
        singleTapTimerRef.current = null
        lastTapRef.current = null
        onSingleTapRef.current()
      }, doubleTapWindowMs - 20)
    }
  }, [clearSingleTapTimer, disabledDoubleTap, doubleTapWindowMs, driftPx, isMobile])

  const onDoubleClick = useCallback(() => {
    if (isMobile) {
      return
    }
    onDesktopDoubleClickRef.current?.()
  }, [isMobile])

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClick,
    onDoubleClick,
  }
}
